import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Loader2,
  BarChart3,
  LineChartIcon,
  AlertTriangle,
  TrendingUp,
  X,
} from "lucide-react";
import { useMemo } from "react";
import { useDatasetExplorerStore } from "../datasetExplorerStore";
import { parseCsv } from "@forgepoint/shared";
import {
  detectColumnTypes,
  buildChartConfig,
  buildChartData,
} from "../utils/chartUtils";
import BarChartView from "./BarChartView";
import LineChartView from "./LineChartView";

/**
 * Chart view for dataset query results.
 * Supports bar and line charts with auto-detected or user-selected axis columns.
 */
export default function ResultsChart() {
  const result = useDatasetExplorerStore((s) => s.result);
  const resultLoading = useDatasetExplorerStore((s) => s.resultLoading);
  const resultError = useDatasetExplorerStore((s) => s.resultError);
  const chartType = useDatasetExplorerStore((s) => s.chartType);
  const setChartType = useDatasetExplorerStore((s) => s.setChartType);
  const chartXAxis = useDatasetExplorerStore((s) => s.chartXAxis);
  const setChartXAxis = useDatasetExplorerStore((s) => s.setChartXAxis);
  const chartYAxes = useDatasetExplorerStore((s) => s.chartYAxes);
  const setChartYAxes = useDatasetExplorerStore((s) => s.setChartYAxes);

  const parsed = useMemo(
    () => (result ? parseCsv(result.csv) : null),
    [result],
  );

  if (resultLoading) {
    return (
      <div className="flex items-center justify-center h-48">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (resultError) {
    return (
      <div className="flex items-center gap-2 rounded-md border border-destructive/50 bg-destructive/5 px-4 py-3 text-sm text-destructive">
        <AlertTriangle className="h-4 w-4 shrink-0" />
        {resultError}
      </div>
    );
  }

  if (!result || !parsed || result.rowCount === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 gap-2 text-muted-foreground">
        <TrendingUp className="h-8 w-8 opacity-30 animate-gentle-float" />
        <p className="text-sm">No data to chart.</p>
      </div>
    );
  }

  const { category, numeric } = detectColumnTypes(parsed.rows, parsed.columns);
  if (numeric.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 gap-2 text-muted-foreground">
        <TrendingUp className="h-8 w-8 opacity-30 animate-gentle-float" />
        <div className="text-center">
          <p className="text-sm font-medium">No numeric columns</p>
          <p className="text-xs mt-1">
            Add aggregate functions to your query to enable charting.
          </p>
        </div>
      </div>
    );
  }

  const autoXAxis = category[0] ?? numeric[0];
  const autoYAxes =
    numeric.filter((n) => n !== autoXAxis).length > 0
      ? numeric.filter((n) => n !== autoXAxis)
      : [numeric[0]];

  const xAxis =
    chartXAxis && parsed.columns.includes(chartXAxis) ? chartXAxis : autoXAxis;
  const yAxes = chartYAxes
    ? chartYAxes.filter((c) => parsed.columns.includes(c))
    : autoYAxes;

  const availableYColumns = parsed.columns.filter((c) => c !== xAxis);
  const chartConfig = buildChartConfig(yAxes);
  const chartData = buildChartData(parsed.rows, parsed.columns, yAxes);

  return (
    <div className="space-y-4">
      <Tabs
        value={chartType}
        onValueChange={(v) => setChartType(v as "bar" | "line")}
      >
        <TabsList className="h-8">
          <TabsTrigger value="bar" className="text-xs px-3 gap-1.5">
            <BarChart3 className="h-3.5 w-3.5" />
            Bar
          </TabsTrigger>
          <TabsTrigger value="line" className="text-xs px-3 gap-1.5">
            <LineChartIcon className="h-3.5 w-3.5" />
            Line
          </TabsTrigger>
        </TabsList>
      </Tabs>

      {/* Axis pickers */}
      <div className="flex flex-wrap items-end gap-4">
        <div className="space-y-1">
          <label className="text-xs font-medium text-muted-foreground">
            X-Axis
          </label>
          <Select value={xAxis} onValueChange={(v) => setChartXAxis(v)}>
            <SelectTrigger className="h-8 w-48 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {parsed.columns.map((col) => (
                <SelectItem key={col} value={col} className="text-xs">
                  {col}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-1">
          <label className="text-xs font-medium text-muted-foreground">
            Y-Axis
          </label>
          <div className="flex items-center gap-2">
            <Select
              value=""
              onValueChange={(v) => {
                const current = chartYAxes ?? autoYAxes;
                if (!current.includes(v)) {
                  setChartYAxes([...current, v]);
                }
              }}
            >
              <SelectTrigger className="h-8 w-48 text-xs">
                <SelectValue placeholder="Add column..." />
              </SelectTrigger>
              <SelectContent>
                {availableYColumns
                  .filter((c) => !yAxes.includes(c))
                  .map((col) => (
                    <SelectItem key={col} value={col} className="text-xs">
                      {col}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
            {yAxes.map((col) => (
              <Badge
                key={col}
                variant="secondary"
                className="text-xs gap-1 pr-1"
              >
                {col}
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-4 w-4 p-0 hover:bg-transparent"
                  onClick={() => {
                    const next = yAxes.filter((c) => c !== col);
                    setChartYAxes(next.length > 0 ? next : null);
                  }}
                >
                  <X className="h-3 w-3" />
                </Button>
              </Badge>
            ))}
          </div>
        </div>
      </div>

      {chartType === "bar" ? (
        <BarChartView
          data={chartData}
          xAxis={xAxis}
          yAxes={yAxes}
          config={chartConfig}
        />
      ) : (
        <LineChartView
          data={chartData}
          xAxis={xAxis}
          yAxes={yAxes}
          config={chartConfig}
        />
      )}
    </div>
  );
}
